import React, { useState } from "react";
import { Icon } from "@iconify/react";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import Breadcrumb from "../../components/shared/Breadcrumb";
import { importDirectorioEmpresas } from "../../platform/apiClient";

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function downloadTemplate() {
  const token = sessionStorage.getItem("syrakis_token");
  const response = await fetch("/api/core-directorio/empresas/plantilla", {
    credentials: "same-origin",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) throw new Error("No se pudo descargar el formato.");
  saveBlob(await response.blob(), "plantilla_empresas.xlsx");
}

function resultRows(rows) {
  return rows.map((row) => ({
    "Estado carga": row.estado_carga,
    Fila: row.fila,
    RUT: row.rut,
    "Razón social": row.razon_social,
    "Nombre fantasía": row.nombre_fantasia,
    Giro: row.giro,
    Detalle: row.detalle,
  }));
}

function downloadResults(rows) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(resultRows(rows));
  XLSX.utils.book_append_sheet(workbook, worksheet, "Resultado carga");
  XLSX.writeFile(workbook, "resultado_carga_empresas.xlsx");
}

export default function CargaMasivaEmpresasPage() {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function readFile(event) {
    const selected = event.target.files?.[0];
    setFile(selected || null);
    setRows([]);
    setStats(null);
    setError("");
    if (!selected) return;
    try {
      const buffer = await selected.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!parsed.length) throw new Error("El archivo no contiene filas.");
      setRows(parsed);
      setMessage(`${parsed.length} filas preparadas para cargar.`);
    } catch (reason) {
      setError(reason.message || "No se pudo leer el archivo Excel.");
      setFile(null);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!rows.length) {
      setError("Selecciona un Excel con empresas antes de cargar.");
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await importDirectorioEmpresas(rows);
      setStats(response.stats);
      setRows(response.results || []);
      setMessage(response.message || "Carga masiva finalizada.");
    } catch (reason) {
      setError(reason.message || "No se pudo procesar la carga masiva.");
    } finally {
      setLoading(false);
    }
  }

  async function getTemplate() {
    setDownloading(true);
    setError("");
    try {
      await downloadTemplate();
    } catch (reason) {
      setError(reason.message || "No se pudo descargar el formato.");
    } finally {
      setDownloading(false);
    }
  }

  const isResult = Boolean(stats);
  return (
    <div className="dashboard-main-body">
      <Breadcrumb title="Carga masiva de empresas" />
      {error && <div className="alert alert-danger mb-20">{error}</div>}
      {message && <div className="alert alert-info mb-20">{message}</div>}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-20">
        <div><h5 className="mb-4">Carga masiva</h5><p className="text-secondary-light mb-0">Carga nuevas empresas y actualiza las existentes usando el RUT.</p></div>
        <Link to="/directorio/empresas" className="btn btn-outline-secondary">Volver a empresas</Link>
      </div>
      <div className="row gy-4">
        <div className="col-lg-5">
          <div className="card h-100"><div className="card-body p-24">
            <h6 className="mb-8">1. Descargar formato</h6>
            <p className="text-secondary-light text-sm mb-16">El formato contiene las columnas necesarias para crear o actualizar empresas.</p>
            <button type="button" className="btn btn-outline-primary d-flex align-items-center gap-2 mb-24" onClick={getTemplate} disabled={downloading}><Icon icon="solar:file-download-linear" />{downloading ? "Descargando..." : "Descargar formato Excel"}</button>
            <h6 className="mb-8">2. Cargar empresas</h6>
            <form onSubmit={submit}>
              <label className="form-label" htmlFor="company-import-file">Archivo Excel</label>
              <input id="company-import-file" type="file" accept=".xlsx,.xls,.csv" className="form-control mb-12" onChange={readFile} disabled={loading} />
              {file?.name && <small className="text-primary-600 d-block mb-8">Archivo seleccionado: {file.name}</small>}
              <small className="text-secondary-light d-block mb-20">Columnas: RUT, Razón Social, Nombre Fantasía y Giro.</small>
              <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" disabled={loading || !rows.length}><Icon icon="solar:upload-minimalistic-linear" />{loading ? "Procesando..." : "Procesar carga"}</button>
            </form>
          </div></div>
        </div>
        <div className="col-lg-7">
          <div className="row gy-4">
            <StatCard label="Empresas cargadas" value={stats?.cargadas ?? "—"} tone="success" />
            <StatCard label="Empresas actualizadas" value={stats?.actualizadas ?? "—"} tone="primary" />
            <StatCard label="Empresas con error" value={stats?.errores ?? "—"} tone="danger" />
            <StatCard label="Total procesadas" value={stats ? stats.total : (rows.length || "—")} tone="warning" />
          </div>
          {isResult && <div className="card mt-24"><div className="card-body p-24"><div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-16"><div><h6 className="mb-4">3. Resultado de carga</h6><p className="text-secondary-light text-sm mb-0">El estado aparece en la primera columna del Excel descargable.</p></div><button type="button" className="btn btn-outline-success d-flex align-items-center gap-2" onClick={() => downloadResults(rows)}><Icon icon="solar:file-download-linear" />Descargar resultado</button></div><div className="table-responsive"><table className="table bordered-table sm-table mb-0"><thead><tr><th>Estado</th><th>Fila</th><th>RUT</th><th>Razón social</th><th>Detalle</th></tr></thead><tbody>{rows.slice(0, 200).map((row) => <tr key={`${row.fila}-${row.rut}`}><td><Status status={row.estado_carga} /></td><td>{row.fila}</td><td>{row.rut || "—"}</td><td>{row.razon_social || "—"}</td><td>{row.detalle || "—"}</td></tr>)}</tbody></table></div>{rows.length > 200 && <small className="text-secondary-light d-block mt-12">Se muestran 200 filas en pantalla. El Excel contiene las {rows.length} filas procesadas.</small>}</div></div>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return <div className="col-md-6"><div className="card p-20 h-100"><span className="text-secondary-light text-sm">{label}</span><h4 className={`mb-0 mt-8 text-${tone}-600`}>{value}</h4></div></div>;
}

function Status({ status }) {
  const config = { cargada: ["Cargada", "success"], actualizada: ["Actualizada", "primary"], error: ["Con error", "danger"] }[status] || [status, "secondary"];
  return <span className={`badge bg-${config[1]}-50 text-${config[1]}-600`}>{config[0]}</span>;
}
